import { useCallback, useMemo, useRef } from 'react';
import {
  Modal,
  ModalSizes,
  Form,
  FormSizes,
  ButtonVariants,
  TextInput,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
  FormValidateOn,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import {
  ReferenceIdField,
  TerminalField,
  DateOfDispatchField,
  VesselNameField,
  ShippingToField,
} from '@/components/common';
import { FACILITY_KINDS, buildFacilityValue } from '@/config/formOptions';
import { outgoingShipmentValidation } from '../outgoingShipmentsValidation';
import { useOutgoingShipmentFormOptions } from '../hooks';
import { ShipmentQuantityField } from './ShipmentQuantityField';
import { ShipmentUnitField } from './ShipmentUnitField';
import styles from './UpdateShipmentModal.module.scss';

const TERMINAL_CLEARS = Object.freeze([]);

/**
 * Props:
 *   open       whether the modal is shown
 *   onClose    ✕ / Cancel
 *   order      the linked-order row the shipment is created for
 *   unitSymbol the unit the table is currently showing — the new shipment inherits it
 *   isSaving   disables Save while the mutation is in flight
 *   onSubmit   (values) => void — runs only once validation passes
 */
export const UpdateShipmentModal = ({ open, onClose, order, unitSymbol, isSaving, onSubmit }) => {
  const t = useTranslations();
  const { options } = useOutgoingShipmentFormOptions();

  // A terminal already set on the order carries over and cannot be changed here.
  const hasOrderTerminal = Boolean(order?.terminalId);

  const initialValues = useMemo(
    () => ({
      idPrefix: 'SP',
      idNumber: '',
      facilityId: order?.siteId ? buildFacilityValue(FACILITY_KINDS.site, order.siteId) : '',
      terminalId: order?.terminalId ? String(order.terminalId) : '',
      materialId: order?.materialId ? String(order.materialId) : '',
      soldToId: order?.customerAccountId ? String(order.customerAccountId) : '',
      shippingToId: order?.shippingToId ? String(order.shippingToId) : '',
      shipmentQuantity: '',
      shipmentQuantityUnit: unitSymbol ?? '',
      displayUnitId: '',
      dateOfDispatch: order?.dispatchDateInput ?? '',
      vesselName: '',
    }),
    [order, unitSymbol],
  );

  const handleSubmit = useCallback((values) => onSubmit?.(values), [onSubmit]);

  // Modal owns the footer: a Form.Footer is sticky inside its scrolling body and floats over fields.
  const submitRef = useRef(null);
  const handleSave = useCallback(() => submitRef.current?.(), []);

  const buttons = useMemo(
    () => [
      { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
      {
        label: t('COMMON_SAVE'),
        variant: ButtonVariants.primary,
        onClick: handleSave,
        disabled: isSaving,
      },
    ],
    [t, onClose, handleSave, isSaving],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={ModalSizes.large}
      title={t('UPDATE_SHIPMENT_TITLE', { orderId: order?.orderNo ?? '' })}
      buttons={buttons}
    >
      <Form
        validateOn={FormValidateOn.change}
        className={styles.form}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        size={FormSizes.large}
      >
        {({ submit }) => {
          submitRef.current = submit;
          return (
            <>
              <div className={styles.intro}>
                <div className={styles.heading}>
                  <Typography
                    variant={TypographyVariants.bodySm}
                    weight={TypographyWeights.semibold}
                    color={TypographyColors.secondary}
                  >
                    {t('UPDATE_SHIPMENT_HEADING')}
                  </Typography>
                </div>
                <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
                  {t('UPDATE_SHIPMENT_DESCRIPTION')}
                </Typography>
              </div>

              <Form.Section>
                <Form.Row columns={1}>
                  <ReferenceIdField
                    label={t('FIELD_SHIPMENT_ID')}
                    // The server generates one when this is left blank.
                    required={false}
                    rules={outgoingShipmentValidation.idNumber}
                  />
                </Form.Row>

                <Form.Row columns={1}>
                  <TextInput
                    label={t('FIELD_MATERIAL_NAME')}
                    value={order?.materialName ?? ''}
                    disabled
                    readOnly
                  />
                </Form.Row>

                <Form.Row columns={2}>
                  <TextInput
                    label={t('FIELD_FACILITY_NAME')}
                    value={order?.facilityName ?? ''}
                    disabled
                    readOnly
                  />

                  <TerminalField
                    sites={options.sites}
                    label={t('FIELD_TERMINAL')}
                    clears={TERMINAL_CLEARS}
                    required
                    disabled={hasOrderTerminal}
                  />
                </Form.Row>

                <Form.Row columns={2}>
                  <TextInput
                    label={t('FIELD_SOLD_TO')}
                    value={order?.soldTo ?? ''}
                    disabled
                    readOnly
                  />

                  <ShippingToField label={t('FORM_SHIPPING_SITES')} />
                </Form.Row>

                <Form.Row columns={2}>
                  <ShipmentQuantityField />

                  <ShipmentUnitField materials={options.materials} label={t('FIELD_UNIT')} />
                </Form.Row>

                <Form.Row columns={2}>
                  <DateOfDispatchField />

                  <VesselNameField />
                </Form.Row>
              </Form.Section>
            </>
          );
        }}
      </Form>
    </Modal>
  );
};
